import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { convertUrlToFilter } from 'utility/Convert/Search/urlToFilter';
import { trackGa4Event } from 'utility/Track/trackingHelper';

import { ResponseStatus } from 'proto/Api/ResponseStatus';
import { ActiveFilter } from 'proto/Search/ActiveFilter';
import { RequestFilterGroup } from 'proto/Search/KiFilter/RequestFilterGroup';

import { getFilters } from '../__helper/getFilters';
import { SearchState, ErrorMessage } from '../types';

export interface UseApplyFilterReturn {
    handleApplyFilter: () => Promise<void>;
    handleCancelRequest: () => void;
    searchState: SearchState;
    errorMessage: ErrorMessage | null;
    filters: ActiveFilter[];
}

export const useApplyFilter = (
    inputValue: string,
    currentUrl: string,
    filterData: () => RequestFilterGroup[],
    updateFilter: (filter: ReturnType<typeof convertUrlToFilter>) => void
): UseApplyFilterReturn => {
    const { t } = useTranslation();
    const [searchState, setSearchState] = useState<SearchState>(
        SearchState.Inactive
    );
    const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null);
    const [filters, setFilters] = useState<ActiveFilter[]>([]);
    const abortControllerRef = useRef<AbortController | undefined>();

    const handleApplyFilter = useCallback(async () => {
        if (searchState !== SearchState.Inactive) {
            return;
        }

        trackGa4Event('kiFilter_click');

        setSearchState(SearchState.Spinner);
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        const timeoutId = setTimeout(() => {
            setSearchState(SearchState.Active);
        }, 1000);

        const response = await getFilters(
            inputValue,
            currentUrl,
            filterData(),
            abortControllerRef.current
        );

        clearTimeout(timeoutId);

        if (!response) {
            return;
        }

        if (response.status === ResponseStatus.Timeout) {
            setErrorMessage({
                title: t('TRANS-network-connection-interrupted'),
                hint: t('TRANS-try-again-at-later-time'),
            });
            setSearchState(SearchState.Inactive);

            return;
        }

        if (response.status === ResponseStatus.Error) {
            setSearchState(SearchState.Inactive);
            setErrorMessage({
                title: t('TRANS-no-suitable-filters-found'),
                hint: t('TRANS-try-again-with-new-formulation'),
            });
        } else if (response.status === ResponseStatus.Success) {
            setErrorMessage(null);
            setSearchState(SearchState.Inactive);
            const decodedUrl = decodeURIComponent(response.portalUrl);
            updateFilter(convertUrlToFilter(decodedUrl));
            const activeFilters = response.activeFilterList || [];
            setFilters(activeFilters);
        }
    }, [t, inputValue, currentUrl, filterData, updateFilter, searchState]);

    const handleCancelRequest = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setSearchState(SearchState.Inactive);
        }
    }, []);

    return {
        handleApplyFilter,
        handleCancelRequest,
        searchState,
        errorMessage,
        filters,
    };
};
